import React from "react";
import { Route } from "react-router-dom";
import MainLayout from "../../components/MainLayout/MainLayout";
import { AuthenticationGuard } from "@/components/AuthenticationGuard/AuthenticationGuard";
import PermissionGuard from "@/components/PermissionGuard/PermissionGuard";
import DashboardPage from "@/modules/stochastic/features/Reports/SCDashboard/components/DashboardPage/DashboardPage";
import { ROIDashboardPage } from "@/modules/stochastic/features/Reports/ROIDashboard/components/ROIDashboardPage/ROIDashboardPage";
import CustomerList from "../../modules/stochastic/features/CustomerList/components/CustomerList/CustomerList";
import InvoiceList from "../../modules/stochastic/features/InvoiceList/components/InvoiceList/InvoiceList";
import DraftInvoiceCalculator from "../../modules/stochastic/features/CampaignBillingManagement/components/DraftInvoiceCalculator/DraftInvoiceCalculator";
import ProspectList from "../../modules/stochastic/features/ProspectList/components/ProspectList/ProspectList";
import DoNotMailList from "@/modules/stochastic/features/DoNotMailManagement/components/DoNotMailList/DoNotMailList";
import CampaignList from "../../modules/stochastic/features/CampaignManagement/components/CampaignList/CampaignList";
import BatchProspectList from "../../modules/stochastic/features/BatchProspectManagement/components/BatchProspectList/BatchProspectList";
import CampaignBatchList from "../../modules/stochastic/features/CampaignBatchManagement/components/CampaignBatchList/CampaignBatchList";
import CampaignBillingList from "../../modules/stochastic/features/CampaignBillingManagement/components/CampaignBillingList/CampaignBillingList";
import CampaignFilesList from "../../modules/stochastic/features/CampaignFileManagement/components/CampaignFilesList/CampaignFilesList";
import { FieldServiceImport } from "@/modules/stochastic/features/FieldServiceImport/components/FieldServiceImport/FieldServiceImport";
import { ProspectSourceImport } from "@/modules/stochastic/features/ProspectSourceImport/components/ProspectSourceImport/ProspectSourceImport";
import ImportStatus from "@/modules/stochastic/features/ImportStatus/components/ImportStatus/ImportStatus";
import CreateCampaignPage from "@/modules/stochastic/features/CampaignManagement/components/CreateCampaignPage/CreateCampaignPage";
import ViewCampaignDetailsPage from "@/modules/stochastic/features/CampaignManagement/components/ViewCampaignDetailsPage/ViewCampaignDetailsPage";
import EditCampaignPage from "@/modules/stochastic/features/CampaignManagement/components/EditCampaignPage/EditCampaignPage";
import CloneCampaignPage from "@/modules/stochastic/features/CampaignManagement/components/CloneCampaign/CloneCampaignPage";
import CampaignProductList from "@/modules/stochastic/features/CampaignProductManagement/components/CampaignProductList/CampaignProductList";
import { PostageExpenseImport } from "@/modules/stochastic/features/PostageExpenseImport/components/PostageExpenseImport/PostageExpenseImport";
import LocationsList from "@/modules/stochastic/features/LocationsList/components/LocationList/LocationsList";
import { DoNotMailListImport } from "@/modules/stochastic/features/DoNotMailImport/components/DoNotMailListImport/DoNotMailListImport";
import CampaignStatusPage from "@/modules/administration/features/Stochastic/CampaignStatus/components/CampaignStatusPage/CampaignStatusPage";
import CampaignJobStatusPage from "@/modules/administration/features/Stochastic/CampaignJobStatus/components/CampaignJobStatusPage/CampaignJobStatusPage";
import SubscriptionBillingPage from "@/modules/stochastic/features/SubscriptionBillingManagement/components/SubscriptionBillingPage/SubscriptionBillingPage";
import TotalAddressableMarketPage from "@/modules/stochastic/features/TotalAddressableMarket/components/TotalAddressableMarketPage/TotalAddressableMarketPage";
import MPDashboardPage from "@/modules/stochastic/features/Reports/MPDashboard/components/MPDashboardPage/MPDashboardPage";
import CategoryList from "@/modules/stochastic/features/ProductStore/components/CategoryList/CategoryList";
import ProductList from "@/modules/stochastic/features/ProductStore/components/ProductList/ProductList";
import ProductCreatePage from "@/modules/stochastic/features/ProductStore/components/ProductCreatePage/ProductCreatePage";
import ProductEditPage from "@/modules/stochastic/features/ProductStore/components/ProductEditPage/ProductEditPage";
import OrderList from "@/modules/stochastic/features/ProductStore/components/OrderList/OrderList";

const StochasticMainLayout = () => <MainLayout section="stochastic" />;

const StochasticRoutes = (
  <Route
    element={<AuthenticationGuard component={StochasticMainLayout} />}
    path="/lead-generation"
  >
    <Route
      element={
        <PermissionGuard requiredPermissions={["stochastic-dashboard"]}>
          <DashboardPage />
        </PermissionGuard>
      }
      path="sales-and-customer-dashboard"
    />

    <Route
      element={
        <PermissionGuard requiredPermissions={["stochastic-roi-dashboard"]}>
          <ROIDashboardPage />
        </PermissionGuard>
      }
      path="roi-dashboard"
    />

    <Route
      element={
        <PermissionGuard requiredPermissions={["stochastic-customers"]}>
          <CustomerList />
        </PermissionGuard>
      }
      path="customers"
    />

    <Route
      element={
        <PermissionGuard requiredPermissions={["stochastic-invoices"]}>
          <InvoiceList />
        </PermissionGuard>
      }
      path="invoices"
    />

    <Route
      element={
        <PermissionGuard requiredPermissions={["administration-stochastic"]}>
          <DraftInvoiceCalculator />
        </PermissionGuard>
      }
      path="draft-invoice"
    />

    <Route
      element={
        <PermissionGuard requiredPermissions={["stochastic-prospects"]}>
          <ProspectList />
        </PermissionGuard>
      }
      path="prospects"
    />

    <Route
      element={
        <PermissionGuard requiredPermissions={["stochastic-locations"]}>
          <LocationsList />
        </PermissionGuard>
      }
      path="locations"
    />

    <Route
      element={
        <PermissionGuard
          requiredPermissions={["stochastic-total-addressable-market"]}
        >
          <TotalAddressableMarketPage />
        </PermissionGuard>
      }
      path="total-addressable-market"
    />

    <Route
      element={
        <PermissionGuard requiredPermissions={["stochastic-mp-dashboard"]}>
          <MPDashboardPage />
        </PermissionGuard>
      }
      path="market-presence"
    />

    <Route
      element={
        <PermissionGuard requiredPermissions={["administration-stochastic"]}>
          <DoNotMailList />
        </PermissionGuard>
      }
      path="do-not-mail"
    />

    <Route
      element={
        <PermissionGuard
          requiredPermissions={["stochastic-campaign-management"]}
        >
          <CampaignList />
        </PermissionGuard>
      }
      path="campaigns"
    />

    <Route
      element={
        <PermissionGuard requiredPermissions={["administration-stochastic"]}>
          <CampaignStatusPage />
        </PermissionGuard>
      }
      path="campaign-status"
    />

    <Route
      element={
        <PermissionGuard requiredPermissions={["administration-stochastic"]}>
          <CampaignJobStatusPage />
        </PermissionGuard>
      }
      path="campaign-job-status"
    />

    <Route
      element={
        <PermissionGuard requiredPermissions={["administration-stochastic"]}>
          <CampaignBillingList />
        </PermissionGuard>
      }
      path="campaigns/metered-billing"
    />

    <Route
      element={
        <PermissionGuard requiredPermissions={["administration-stochastic"]}>
          <SubscriptionBillingPage />
        </PermissionGuard>
      }
      path="campaigns/subscription-billing"
    />

    <Route
      element={
        <PermissionGuard
          requiredPermissions={["stochastic-campaign-management"]}
        >
          <CampaignBatchList />
        </PermissionGuard>
      }
      path="campaigns/:campaignId/batches"
    />
    <Route
      element={
        <PermissionGuard
          requiredPermissions={["stochastic-campaign-management"]}
        >
          <BatchProspectList />
        </PermissionGuard>
      }
      path="campaigns/:campaignId/batches/:batchId/prospects"
    />

    {/* Files, FieldServiceImport, ProspectSourceImport require stochastic-uploads permission */}
    <Route
      element={
        <PermissionGuard requiredPermissions={["administration-stochastic"]}>
          <CampaignFilesList />
        </PermissionGuard>
      }
      path="campaigns/:campaignId/files"
    />
    <Route
      element={
        <PermissionGuard requiredPermissions={["stochastic-uploads"]}>
          <FieldServiceImport />
        </PermissionGuard>
      }
      path="field-service-import"
    />
    <Route
      element={
        <PermissionGuard requiredPermissions={["stochastic-uploads"]}>
          <ProspectSourceImport />
        </PermissionGuard>
      }
      path="prospect-source-import"
    />
    <Route
      element={
        <PermissionGuard requiredPermissions={["stochastic-uploads"]}>
          <DoNotMailListImport />
        </PermissionGuard>
      }
      path="do-not-mail-import"
    />

    <Route
      element={
        <PermissionGuard requiredPermissions={["stochastic-uploads"]}>
          <ImportStatus />
        </PermissionGuard>
      }
      path="import-status"
    />

    <Route
      element={
        <PermissionGuard
          requiredPermissions={["stochastic-campaign-management"]}
        >
          <CreateCampaignPage />
        </PermissionGuard>
      }
      path="campaigns/new"
    />

    <Route
      element={
        <PermissionGuard
          requiredPermissions={["stochastic-campaign-management"]}
        >
          <ViewCampaignDetailsPage />
        </PermissionGuard>
      }
      path="campaigns/:campaignId/view"
    />

    <Route
      element={
        <PermissionGuard
          requiredPermissions={["stochastic-campaign-management"]}
        >
          <EditCampaignPage />
        </PermissionGuard>
      }
      path="campaigns/:campaignId/edit"
    />

    <Route
      element={
        <PermissionGuard
          requiredPermissions={["stochastic-campaign-management"]}
        >
          <CloneCampaignPage />
        </PermissionGuard>
      }
      path="campaigns/:id/clone"
    />

    <Route
      element={
        <PermissionGuard requiredPermissions={["administration-stochastic"]}>
          <CampaignProductList />
        </PermissionGuard>
      }
      path="products/campaign"
    />

    <Route
      element={
        <PermissionGuard requiredPermissions={["administration-stochastic"]}>
          <PostageExpenseImport />
        </PermissionGuard>
      }
      path="campaigns/postage"
    />

    <Route
      element={
        <PermissionGuard requiredPermissions={["administration-product-store"]}>
          <CategoryList />
        </PermissionGuard>
      }
      path="product-store/categories"
    />

    <Route
      element={
        <PermissionGuard requiredPermissions={["administration-product-store"]}>
          <ProductList />
        </PermissionGuard>
      }
      path="product-store/products"
    />
    <Route
      element={
        <PermissionGuard requiredPermissions={["administration-product-store"]}>
          <ProductCreatePage />
        </PermissionGuard>
      }
      path="product-store/products/new"
    />
    <Route
      element={
        <PermissionGuard requiredPermissions={["administration-product-store"]}>
          <ProductEditPage />
        </PermissionGuard>
      }
      path="product-store/products/:id/edit"
    />
    <Route
      element={
        <PermissionGuard requiredPermissions={["administration-product-store"]}>
          <OrderList />
        </PermissionGuard>
      }
      path="product-store/orders"
    />
  </Route>
);

export default StochasticRoutes;
